import React from "react";
import { Button } from "@heroui/react";
import { motion } from "framer-motion";
import { Icon } from "@iconify/react";
import { Link as RouterLink } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, EffectFade } from "swiper/modules";
import type { Swiper as SwiperType } from "swiper";
import "swiper/css";
import "swiper/css/effect-fade";
import heroBackground from "../assets/images/hero-background.png";
import heroNewYearBackground from "../assets/images/hero-new-year-background.png";

export const Hero: React.FC = () => {
  const SLIDE_DURATION = 5000; // 8 секунд
  const SLIDE_FADE_DURATION = 1400; // более плавный переход между слайдами
  const PREVIEW_THRESHOLD_MS = 1600; // когда начинать прогрев анимации текста

  const slides = React.useMemo(() => [
    // {
    //   id: "new-year",
    //   background: heroNewYearBackground,
    //   title: (
    //     <>
    //       Новогодняя коллекция <br />
    //       <span className="font-semibold">атмосфера праздника в каждом аромате</span>
    //     </>
    //   ),
    //   description:
    //     "Лимитированные ароматы, подарочные наборы и свечи для создания новогоднего настроения дома и в подарках.",
    //   primaryCta: {
    //     label: "Смотреть новогоднюю коллекцию",
    //     to: "/catalog/collection?new_year=true",
    //   },
    //   secondaryCta: {
    //     label: "Весь каталог",
    //     to: "/catalog",
    //   },
    // },
    {
      id: "main",
      background: heroBackground,
      title: (
        <>
          Создайте атмосферу <br />
          <span className="font-semibold">с ароматами ручной работы</span>
        </>
      ),
      description:
        "Премиальные ароматические свечи и диффузоры из экологичных материалов для вашего дома и бизнеса",
      primaryCta: {
        label: "Перейти в каталог",
        to: "/catalog",
      },
      secondaryCta: {
        label: "Для бизнеса",
        to: "/partners",
      },
    },
  ], []);

  const [currentSlide, setCurrentSlide] = React.useState(0);
  const [progress, setProgress] = React.useState(0);
  const totalSlides = slides.length;
  const swiperRef = React.useRef<SwiperType | null>(null);
  const [incomingSlide, setIncomingSlide] = React.useState<number | null>(null);
  const textVariants = React.useMemo(
    () => ({
      hidden: {
        opacity: 0,
        x: 90,
        filter: "blur(14px)",
        transition: { duration: 0.85, ease: [0.4, 0, 0.2, 1] },
      },
      preVisible: {
        opacity: 0.5,
        x: 35,
        filter: "blur(8px)",
        transition: { duration: 0.9, ease: [0.25, 0.8, 0.5, 1] },
      },
      visible: {
        opacity: 1,
        x: 0,
        filter: "blur(0px)",
        transition: { duration: 1.3, ease: [0.19, 1, 0.22, 1] },
      },
    }),
    []
  );

  // Предзагрузим фоновые изображения один раз
  React.useEffect(() => {
    const loaders: HTMLImageElement[] = slides.map((slide) => {
      const img = new Image();
      img.src = slide.background || heroBackground;
      return img;
    });
    return () => {
      loaders.forEach((img) => {
        img.onload = null;
        img.onerror = null;
      });
    };
  }, [slides]);
  const handleAutoplayTimeLeft = React.useCallback(
    (swiper: SwiperType, time: number, progressValue: number) => {
      const percent = Math.min(Math.max(1 - progressValue, 0), 1) * 100;
      setProgress(percent);

      if (!swiper || totalSlides === 0) return;
      if (time <= PREVIEW_THRESHOLD_MS) {
        const nextIndex = ((swiper.realIndex ?? swiper.activeIndex ?? 0) + 1) % totalSlides;
        if (incomingSlide !== nextIndex) {
          setIncomingSlide(nextIndex);
        }
      } else if (incomingSlide !== null) {
        setIncomingSlide(null);
      }
    },
    [incomingSlide, totalSlides, PREVIEW_THRESHOLD_MS]
  );

  const goToSlide = (index: number) => {
    if (!swiperRef.current) return;
    swiperRef.current.slideToLoop(index);
  };

  return (
    <section
      className="hero-image relative flex min-h-[680px] md:min-h-[780px] lg:min-h-[860px] items-center overflow-hidden"
      id="home"
    >
      <Swiper
        modules={[Autoplay, EffectFade]}
        effect="fade"
        fadeEffect={{ crossFade: true }}
        loop
        speed={SLIDE_FADE_DURATION}
        autoplay={{ delay: SLIDE_DURATION, disableOnInteraction: false }}
        onSwiper={(swiper) => {
          swiperRef.current = swiper;
        }}
        onSlideChange={(swiper) => {
          setCurrentSlide(swiper.realIndex ?? swiper.activeIndex ?? 0);
          setIncomingSlide(null);
        }}
        onAutoplayTimeLeft={handleAutoplayTimeLeft}
        className="w-full h-full"
      >
        {slides.map((slide, index) => (
          <SwiperSlide key={slide.id}>
            <div
              className="relative flex min-h-[680px] md:min-h-[780px] lg:min-h-[860px] items-center"
              style={{
                backgroundImage: `linear-gradient(180deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.35) 100%), url(${slide.background})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat",
              }}
            >
              <div className="container mx-auto px-6 md:px-12 py-24 md:py-32 relative">
                <div className="max-w-3xl">
                  <motion.div
                    variants={textVariants}
                    initial="hidden"
                    animate={
                      currentSlide === index
                        ? "visible"
                        : incomingSlide === index
                          ? "preVisible"
                          : "hidden"
                    }
                  >
                    <h1 className="text-4xl md:text-5xl lg:text-6xl font-light mb-6 text-white text-shadow">
                      {slide.title}
                    </h1>
                    <p className="text-lg md:text-xl text-white/90 mb-8 max-w-xl text-shadow">
                      {slide.description}
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4">
                      <Button
                        color="primary"
                        size="lg"
                        variant="solid"
                        className="font-medium text-base"
                        endContent={<Icon icon="lucide:arrow-right" width={20} />}
                        as={RouterLink}
                        to={slide.primaryCta.to}
                      >
                        {slide.primaryCta.label}
                      </Button>
                      {slide.secondaryCta && (
                        <Button
                          color="default"
                          size="lg"
                          variant="bordered"
                          className="font-medium text-base border-white/70 text-white"
                          as={RouterLink}
                          to={slide.secondaryCta.to}
                        >
                          {slide.secondaryCta.label}
                        </Button>
                      )}
                    </div>
                  </motion.div>
                </div>
              </div>
            </div>
          </SwiperSlide>
        ))}
      </Swiper>

      {/* Слайдер навигация */}
      <div className="absolute inset-y-0 left-0 right-0 flex items-center justify-between px-4 pointer-events-none z-20">
        <button
          type="button"
          className="hidden sm:flex items-center justify-center w-12 h-12 rounded-full bg-black/10 text-white pointer-events-auto hover:bg-black/30 transition border border-white/30"
          onClick={() => swiperRef.current?.slidePrev()}
          aria-label="Предыдущий слайд"
        >
          <Icon icon="lucide:chevron-left" width={24} />
        </button>
        <button
          type="button"
          className="hidden sm:flex items-center justify-center w-12 h-12 rounded-full bg-black/10 text-white pointer-events-auto hover:bg-black/30 transition border border-white/30"
          onClick={() => swiperRef.current?.slideNext()}
          aria-label="Следующий слайд"
        >
          <Icon icon="lucide:chevron-right" width={24} />
        </button>
      </div>

      {/* Прогресс-бар перенесен наверх */}
      <div className="absolute left-0 right-0 top-0 z-20">
        <div className="w-full h-0.5 bg-white/5 overflow-hidden">
          <div
            className="h-full bg-white/30 transition-[width] duration-100 ease-linear"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      <div className="absolute bottom-8 left-0 right-0 flex justify-center z-20">
        <div className="flex flex-col items-center gap-3">
          <div className="flex gap-2">
            {slides.map((slide, index) => (
              <button
                key={slide.id}
                type="button"
                onClick={() => goToSlide(index)}
                className={`w-2.5 h-2.5 rounded-full border border-white/60 transition ${
                  index === currentSlide ? "bg-white" : "bg-transparent"
                }`}
                aria-label={`Перейти к слайду ${index + 1}`}
              />
            ))}
          </div>
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              duration: 0.5,
              ease: "easeOut",
              delay: 1.5,
              repeat: Infinity,
              repeatType: "reverse",
              repeatDelay: 0.5,
            }}
          >
            <a href="#catalog" className="text-white/80 hover:text-white">
              <Icon icon="lucide:chevron-down" width={36} />
            </a>
          </motion.div>
        </div>
      </div>
    </section>
  );
};